import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Input,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { UserFilterForm } from '../../../form-model/user-filter.form.model';

import '@maersk-global/mds-components-core/mc-multi-select';
import '@maersk-global/mds-components-core/mc-input';
import '@maersk-global/mds-components-core/mc-checkbox';
import { USER_FILTER_CONFIG } from '../user-grid/user-config.component';
import { UserManagementService } from '../../../services/user-management.service';
import { AemsMultilineTagInputComponent } from '../../../shared-components/aems-multiline-tag-input/aems-multiline-tag-input.component';
import { RegexConstants } from '../../../RegexConstants';
@Component({
  selector: 'app-user-advanced-filters',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, AemsMultilineTagInputComponent],
  templateUrl: './user-advanced-filters.component.html',
  styleUrl: './user-advanced-filters.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class UserAdvancedFiltersComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) userFilterForm!: FormGroup<UserFilterForm>;
  @Input() activeTab!: number;
  @Input() bindMultiSelects: boolean = false;
  visibleFilters: string[] = [];
  rolesList: any[] = [];
  regionList: any[] = [];
  clusterList: any[] = [];
  shopCodeList: any[] = [];
  shopCodeRegex = RegexConstants.SHOP_CODE;

  private readonly destroyed$ = new Subject<void>();

  constructor(
    private readonly fb: FormBuilder,
    private readonly usermanagementService: UserManagementService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.setVisibleFilters();
    this.loadRoles();
    this.handleRegionChanges();
    this.handleClusterChanges();

    if (this.activeTab === 0) {
      // Eagerly populate shop list; getShops() is shareReplay(1) so only one HTTP call ever.
      //this.loadAllShops();
    } else {
      // Pre-warm the regions cache now so the HTTP call is complete before
      // the user opens the filter modal. getRegions() is shareReplay(1).
      this.loadRegions();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // Only recompute visible-filter list when the tab actually changed
    if (changes['activeTab']) {
      this.setVisibleFilters();
    }

    // When the modal opens, ensure the relevant list is populated.
    if (changes['bindMultiSelects']?.currentValue === true) {
      if (this.activeTab !== 0) {
        // Non-shop tab: load regions on first modal open
        this.loadRegions();
      }
    }

    // Tab switch: reset cascaded lists and text input
    if (changes['activeTab'] && !changes['activeTab'].firstChange) {
      this.clusterList = [];
      this.shopCodeList = [];

      if (this.activeTab !== 0) {
        // Always pre-warm regions cache on tab switch.
        this.loadRegions();
      }
    }
  }
  private setVisibleFilters() {
    this.visibleFilters = this.activeTab === 0 ? USER_FILTER_CONFIG.SHOP : USER_FILTER_CONFIG.NON_SHOP;
  }
  private loadRoles() {
    this.usermanagementService.getRoles().subscribe((res) => {
      this.rolesList = res.map((r) => ({
        label: r.description,
        value: r.description, // send role name to backend, not numeric id
      }));
      this.cdr.markForCheck();
    });
  }
  private loadRegions() {
    this.usermanagementService.getRegions().subscribe((res) => {
      this.regionList = res.map((r) => ({
        label: r.description,
        value: r.code,
      }));
      this.cdr.markForCheck();
    });
  }

  private handleRegionChanges() {
    this.userFilterForm
      .get('region')
      ?.valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((regions) => {
        if (this.activeTab === 0) return;

        this.clusterList = [];
        this.shopCodeList = [];
        this.userFilterForm.get('cluster')?.setValue([], { emitEvent: false });
        this.userFilterForm.get('shopCode')?.setValue([], { emitEvent: false });
        this.cdr.markForCheck();

        if (regions?.length) {
          this.loadClusters(regions.join(','));
        }
      });
  }
  private loadClusters(regionCode: string) {
    this.usermanagementService.getClusters(regionCode).subscribe((res) => {
      this.clusterList = res.map((c) => ({
        label: c.description,
        value: c.code,
      }));
      this.cdr.markForCheck();
    });
  }
  private handleClusterChanges() {
    this.userFilterForm
      .get('cluster')
      ?.valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((clusters) => {
        if (this.activeTab === 0) return;

        this.shopCodeList = [];
        this.userFilterForm.get('shopCode')?.setValue([], { emitEvent: false });
        this.cdr.markForCheck();

        if (clusters?.length) {
          this.loadShops(clusters.join(','));
        }
      });
  }
  private loadShops(clusterCode: string) {
    this.usermanagementService.getShopsByCluster(clusterCode).subscribe((res) => {
      this.shopCodeList = res.map((s) => ({
        label: `${s.code} - ${s.description}`,
        value: s.code,
      }));
      this.cdr.markForCheck();
    });
  }

  applyFilters() {
    this.userFilterForm.controls.applyUserFilter.setValue(true);
  }

  clearFilters() {
    this.userFilterForm.reset();
    this.userFilterForm.controls.applyUserFilter.setValue(true);
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  isValueSelected(value: boolean): boolean {
    const arr = this.userFilterForm.get('isActive')?.value || [];
    return arr.includes(value);
  }

  onCheckChange(value: boolean): void {
    const control = this.userFilterForm.get('isActive');
    const current = control?.value ?? [];

    if (current.includes(value)) {
      control?.setValue(current.filter((v: boolean) => v !== value));
    } else {
      control?.setValue([...current, value]);
    }
  }
}
